import type { PersoneelsdienstenModel } from "@personeel/models/PersoneelsdienstenModel";
import { useKlusSoortenStore } from "@personeel/stores/KlusSoortenStore.ts";
import {
  arraysAreEqual,
  compareKlussenBySortState,
  isKlusEindLocatieAnders,
  SortState,
} from "@personeel/utils/KlusUtils.ts";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId.ts";
import { DATETIME_FORMAT_NO_DATE_NO_SECONDS } from "@shared/common/utils/luxonHelpers";
import { useMessageBannerStore } from "@shared/shared-zustand/MessageBannerStore.ts";
import "@tractie/components";
import "@tractie/components/styles.css";
import { DateTime, Settings } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isValidationError,
  useAddOrModifyKlus,
  useDeleteKlus,
  useFetchKlussenOverview,
  useFetchKlussoorten,
  useFetchLocaties,
  useFetchPersoneelsdiensten,
  useFetchRangeerbewegingen,
  ValidationError,
} from "../../hooks/useFetchData.ts";
import { buildKlusModel, CreateKlusModel } from "../../models/CreateKlusModel";
import type { KlusModel } from "../../models/KlusModel.ts";
import { useKlussenStore } from "../../stores/KlussenStore.ts";
import { toStringDate } from "../../utils/ToStringDate.ts";
import { CreateKlus } from "../CreateKlus/CreateKlus.tsx";
import { DeleteKlusDialog } from "../DeleteKlus/DeleteKlusDialog";
import type { DienstOptie } from "./DienstOptie.tsx";
import { filterDienstenVoorKlussen, mapWerkperiodeToDienstOptie } from "./DienstUtils";
import "./KlussenOverview.css";
import { KlussenOverviewHeader } from "./KlussenOverviewHeader/KlussenOverviewHeader.tsx";

interface ErrorI {
  scenarioId: string | undefined;
  name: string;
  error: Error;
}

interface OverlayMixinElement extends HTMLElement {
  toggleContainer(open: boolean): void;
}

interface SidePanelToggleEvent extends Event {
  newState?: "open" | "closed";
}

type CheckboxElement = HTMLElement & {
  checked: boolean;
};

type ModelValueChangedDetail = {
  isTriggeredByUser?: boolean;
};

function getUserTriggeredCheckboxFromEvent(event: CustomEvent<never>): CheckboxElement | null {
  const details = event.detail as ModelValueChangedDetail | undefined;
  if (!details?.isTriggeredByUser) {
    return null;
  }

  return event.currentTarget as CheckboxElement | null;
}

function createValidationMessage(scenarioId: string | null, error: ValidationError) {
  if (!error.issues || error.issues.length === 0) {
    return null;
  }

  console.log("ValidationError voor scenario: ", scenarioId);
  console.log(error.issues.map((issue) => issue.path.join(".") + "-" + issue.message).join("\n"));

  return (
    <>
      {error.issues.map((issue) => {
        return (
          <li key={issue.path.join(".") + "-" + issue.message}>
            <strong>{issue.path.join(".")}</strong>: {issue.message}
          </li>
        );
      })}
    </>
  );
}

function createErrorMessages(scenarioId: string | undefined, fq: ErrorI[]) {
  for (const q of fq) {
    if (q.error && !isValidationError(q.error) && q.error?.message) {
      console.log("scenario: ", scenarioId, " -- ", q.name, ":", q.error.message);
    }
  }

  return (
    <>
      <ul style={{ color: "red" }}>
        {fq
          .filter((q) => q.error && !isValidationError(q.error) && q.error?.message)
          .map((q) => (
            <li key={q.name}>
              scenario: {scenarioId} -- {q.name}: {q.error.message}
            </li>
          ))}
      </ul>
      {fq
        .filter((q) => q.error && isValidationError(q.error))
        .map((q) => (
          <ul style={{ color: "red" }} key={q.name}>
            scenario: {scenarioId} -- {q.name}-validationerrors:{" "}
            {createValidationMessage(scenarioId!, q.error)}
          </ul>
        ))}
    </>
  );
}

export function KlussenOverview() {
  Settings.defaultLocale = "nl";
  const instanceId = useCurrentInstanceId((state) => state.instanceId);

  const fetchQueries = [
    {
      name: "fetchKlussenOverview",
      ...useFetchKlussenOverview(),
    },
    {
      name: "fetchKlusSoorten",
      ...useFetchKlussoorten(),
    },
    {
      name: "fetchPersoneelsdiensten",
      ...useFetchPersoneelsdiensten(),
    },
    {
      name: "fetchRangeerbewegingen",
      ...useFetchRangeerbewegingen(),
    },
    {
      name: "fetchLocaties",
      ...useFetchLocaties(),
    },
  ];

  const personeelsdienstenQuery = fetchQueries.find((q) => q.name === "fetchPersoneelsdiensten");
  const personeelsdiensten = personeelsdienstenQuery?.data as PersoneelsdienstenModel[] | undefined;
  const isOpenMessageBar = useMessageBannerStore((store) => store.open);

  const {
    mutate: deleteKlus,
    isError: isErrorDeleteKlus,
    error: errorDeleteKlus,
    reset: resetDeleteKlus,
  } = useDeleteKlus();
  const {
    mutate: addOrModifyKlus,
    isError: isErrorModifyKlus,
    error: errorModifyKlus,
    reset: resetModifyKlus,
  } = useAddOrModifyKlus();

  const sidepanelRef = useRef<OverlayMixinElement>(null);
  const initialRunScenario = useRef(true);
  const [isSidepanelOpen, setIsSidepanelOpen] = useState(false);

  const setSidepanelRef = (sidepanelElement: OverlayMixinElement | null) => {
    sidepanelRef.current = sidepanelElement;
    // initieel moet het sidepanel gesloten zijn
    if (initialRunScenario.current && sidepanelElement) {
      sidepanelElement.toggleContainer(false);
      initialRunScenario.current = false;
    }
  };

  const klussenOverviewItems = useKlussenStore((state) => state.klussen);
  const klusSoorten = useKlusSoortenStore((state) => state.klusSoorten);
  const [updatableKlus, setUpdatableKlus] = useState<CreateKlusModel>(new CreateKlusModel());

  const handleCloseSidepanel = useCallback(() => {
    setIsSidepanelOpen(false);
    setUpdatableKlus(new CreateKlusModel());
    sidepanelRef.current?.toggleContainer(false);
  }, []);

  useEffect(() => {
    if (isErrorModifyKlus && errorModifyKlus) {
      // nu even een alert, is dat genoeg of echt modal popupje ofzo?
      alert(`Er is een fout opgetreden: ${errorModifyKlus.message || "Onbekende fout"}`);
      // Reset de errorstatus zodat de alert niet blijft terugkomen
      resetModifyKlus();
    }
  }, [isErrorModifyKlus, errorModifyKlus, resetModifyKlus]);

  useEffect(() => {
    const sidepanelElement = sidepanelRef.current;

    if (!sidepanelElement) {
      return;
    }

    const handleToggle = (event: Event) => {
      const sidePanelToggleEvent = event as SidePanelToggleEvent;

      if (sidePanelToggleEvent.newState === "open") {
        setIsSidepanelOpen(true);
      }

      if (sidePanelToggleEvent.newState === "closed") {
        setIsSidepanelOpen(false);
        setUpdatableKlus(new CreateKlusModel());
      }
    };

    sidepanelElement.addEventListener("toggle", handleToggle);

    return () => {
      sidepanelElement.removeEventListener("toggle", handleToggle);
    };
  }, [handleCloseSidepanel]);

  const [sortState, setSortState] = useState<SortState>({
    properties: ["beginDatumtijd"],
    order: "asc",
  });

  const onSort = (properties: (keyof KlusModel)[]) => {
    let order: string = "asc";
    if (arraysAreEqual(sortState.properties, properties) && sortState.order === "asc") {
      order = "desc";
    }
    setSortState({ properties, order });
  };

  const sortedKlussen = useMemo(() => {
    if (!sortState.properties || !sortState.order) return klussenOverviewItems;
    return [...klussenOverviewItems]
      .filter((k) => !k.klusSoort.startsWith("LOPEN_"))
      .sort(compareKlussenBySortState(sortState));
  }, [klussenOverviewItems, sortState]);

  const [selectedKlusIds, setSelectedKlusIds] = useState<Set<string | number>>(new Set());

  const selectedVisibleKlussen: KlusModel[] = useMemo(
    () => sortedKlussen.filter((k) => (k.id ? selectedKlusIds.has(k.id) : false)),
    [sortedKlussen, selectedKlusIds],
  );

  const mogelijkeDiensten: DienstOptie[] = useMemo(() => {
    if (!personeelsdiensten) return [];
    if (selectedVisibleKlussen.length === 0) return [];

    const relevanteDienstenVoorKlusperiode = filterDienstenVoorKlussen(
      selectedVisibleKlussen,
      personeelsdiensten,
    );
    const dienstOpties = relevanteDienstenVoorKlusperiode.map(mapWerkperiodeToDienstOptie);

    const heeftAlDienst = selectedVisibleKlussen.some((k) => !!k.dienst);

    if (heeftAlDienst) {
      return [
        {
          id: "UNASSIGNED",
          value: "NIET_TOEGEWEZEN",
          label: "Niet toegewezen",
        },
        ...dienstOpties,
      ];
    }

    return dienstOpties;
  }, [selectedVisibleKlussen, personeelsdiensten]);

  // deze moeten we eigenlijk refactoren zodat onze backend gewoon correcte dienst IDs terugstuurt.
  const dienstIdNaarLabel = useMemo(() => {
    if (!personeelsdiensten) return new Map<string, string>();

    const map = new Map<string, string>();

    personeelsdiensten.forEach((pdd) => {
      pdd.personeelsdiensten.forEach((werkperiode) => {
        const optie = mapWerkperiodeToDienstOptie(werkperiode);
        map.set(optie.id, optie.label);
      });
    });

    return map;
  }, [personeelsdiensten]);

  const assignDienstToSelected = (dienstOptie: DienstOptie) => {
    if (selectedKlusIds.size === 0) return;

    const isUnassign = dienstOptie.value === "NIET_TOEGEWEZEN";

    const klussenToUpdate = sortedKlussen.filter((k) => k.id && selectedKlusIds.has(k.id));

    klussenToUpdate.forEach((klus) => {
      const updatedKlus: KlusModel = {
        ...klus,
        dienst: isUnassign ? null : dienstOptie.value,
      };

      addOrModifyKlus(updatedKlus);
    });

    setSelectedKlusIds(new Set());
  };

  const isAllKlussenSelected =
    sortedKlussen.length > 0 && sortedKlussen.every((k) => k.id && selectedKlusIds.has(k.id));

  const handleSelectAllKlussenChange = useCallback(
    (event: CustomEvent<never>) => {
      const checkbox = getUserTriggeredCheckboxFromEvent(event);
      if (!checkbox) {
        return;
      }

      if (!checkbox.checked) {
        setSelectedKlusIds(new Set());
        return;
      }

      const allVisibleIds = sortedKlussen
        .map((k) => k.id)
        .filter((id): id is number => id !== undefined);
      setSelectedKlusIds(new Set(allVisibleIds));
    },
    [sortedKlussen],
  );

  const handleKlusSelectionChange = useCallback(
    (id: string | number | undefined, event: CustomEvent<never>) => {
      if (!id) {
        return;
      }

      const checkbox = getUserTriggeredCheckboxFromEvent(event);
      if (!checkbox) {
        return;
      }

      setSelectedKlusIds((currentSelection) => {
        const updatedSelection = new Set(currentSelection);

        if (checkbox.checked) {
          updatedSelection.add(id);
        } else {
          updatedSelection.delete(id);
        }

        return updatedSelection;
      });
    },
    [],
  );

  useEffect(() => {
    if (isErrorDeleteKlus && errorDeleteKlus) {
      // nu even een alert, is dat genoeg of echt modal popupje ofzo?
      alert(`Er is een fout opgetreden: ${errorDeleteKlus.message || "Onbekende fout"}`);
      // Reset de errorstatus zodat de alert niet blijft terugkomen
      resetDeleteKlus();
    }
  }, [isErrorDeleteKlus, errorDeleteKlus, resetDeleteKlus]);

  const addKlus = (createKlus: CreateKlusModel) => {
    addOrModifyKlus(buildKlusModel(createKlus));
    handleCloseSidepanel();
  };

  const [klusToDelete, setKlusToDelete] = useState<KlusModel | null>(null);

  if (fetchQueries.some((fq) => fq.error)) {
    const errors = fetchQueries
      .filter((q) => q.isError)
      .map((q) => ({ scenarioId: instanceId, name: q.name, error: q.error }));
    return createErrorMessages(instanceId, errors);
  }

  const createKlusHandler = () => {
    setUpdatableKlus(new CreateKlusModel());
    setIsSidepanelOpen(true);
    sidepanelRef.current?.toggleContainer(true);
  };

  const renderSortIcon = (key: (keyof KlusModel)[]) => {
    if (!arraysAreEqual(sortState.properties, key)) {
      return <tractie-icon name="sort" />;
    } else if (sortState.order === "asc") {
      return <tractie-icon name="sort-down" />;
    } else if (sortState.order === "desc") {
      return <tractie-icon name="sort-up" />;
    }
    return <tractie-icon name="sort" />;
  };

  return (
    <div className="klussen-overview-layout" data-testid="page-klussen-overview">
      <tractie-layout orientation="horizontal" className={"klussen-overview-layout"}>
        <tractie-layout-slot colspan={2}>
          <KlussenOverviewHeader
            createKlus={createKlusHandler}
            selectedCount={selectedKlusIds.size}
            mogelijkeDiensten={mogelijkeDiensten}
            onAssignDienst={assignDienstToSelected}
          />
          <div
            className={"klussen-overview-table-scrollable"}
            data-testid="container-klussen-table-scroll"
            style={{ maxHeight: "calc(-" + (isOpenMessageBar ? 201 : 153) + "px + 100svh)" }}
          >
            <table data-testid="table-klussen">
              <thead>
                <tr>
                  <th className="col-select">
                    <tractie-checkbox
                      class="klus-select-checkbox"
                      id="select-all-klussen"
                      data-testid="checkbox-select-all-klussen"
                      label=""
                      checked={isAllKlussenSelected}
                      onmodel-value-changed={handleSelectAllKlussenChange}
                    />
                  </th>
                  <th
                    onClick={() => onSort(["klusSoort"])}
                    className={"table-action-button"}
                    data-testid="sort-klus"
                  >
                    Klus {renderSortIcon(["klusSoort"])}
                  </th>
                  <th
                    onClick={() => onSort(["beginDatumtijd"])}
                    className={"table-action-button"}
                    data-testid="sort-start"
                  >
                    Start {renderSortIcon(["beginDatumtijd"])}
                  </th>
                  <th
                    onClick={() => onSort(["eindDatumtijd"])}
                    className={"table-action-button"}
                    data-testid="sort-eind"
                  >
                    Eind {renderSortIcon(["eindDatumtijd"])}
                  </th>
                  <th
                    onClick={() => onSort(["dienst"])}
                    className={"table-action-button"}
                    data-testid="sort-dienst"
                  >
                    Dienst {renderSortIcon(["dienst"])}
                  </th>
                  <th
                    onClick={() => onSort(["bewegingsCode"])}
                    className={"table-action-button"}
                    data-testid="sort-treinnr"
                  >
                    Treinnr.{renderSortIcon(["bewegingsCode"])}
                  </th>
                  <th
                    onClick={() => onSort(["dienstregelpuntStart", "spoorStart"])}
                    className={"table-action-button"}
                    data-testid="sort-startlocatie"
                  >
                    Startlocatie {renderSortIcon(["dienstregelpuntStart", "spoorStart"])}
                  </th>
                  <th
                    onClick={() => onSort(["dienstregelpuntEind", "spoorEind"])}
                    className={"table-action-button"}
                    data-testid="sort-eindlocatie"
                  >
                    Eindlocatie {renderSortIcon(["dienstregelpuntEind", "spoorEind"])}
                  </th>
                  <th
                    onClick={() => onSort(["toelichting"])}
                    className={"table-action-button"}
                    data-testid="sort-toelichting"
                  >
                    Toelichting {renderSortIcon(["toelichting"])}
                  </th>
                  <th>Actie</th>
                </tr>
              </thead>
              <tbody>
                {sortedKlussen.map((k: KlusModel) => {
                  const isSelected = k.id ? selectedKlusIds.has(k.id) : false;
                  return (
                    <tr
                      key={k.id}
                      className={isSelected ? "klus-row selected" : "klus-row"}
                      data-testid={`row-klus-${k.id}`}
                    >
                      <td className="col-select">
                        <tractie-checkbox
                          class="klus-select-checkbox"
                          id={`select-klus-${k.id}`}
                          data-testid={`checkbox-klus-${k.id}`}
                          label=""
                          checked={isSelected}
                          onmodel-value-changed={(event) => handleKlusSelectionChange(k.id, event)}
                        />
                      </td>
                      <td className="klus-cell">{k.klusSoort}</td>
                      <td>
                        {DateTime.fromISO(toStringDate(k.beginDatumtijd)).toFormat(
                          DATETIME_FORMAT_NO_DATE_NO_SECONDS,
                        )}
                      </td>
                      <td>
                        {DateTime.fromISO(toStringDate(k.eindDatumtijd)).toFormat(
                          DATETIME_FORMAT_NO_DATE_NO_SECONDS,
                        )}
                      </td>
                      <td>{k.dienst ? (dienstIdNaarLabel.get(k.dienst) ?? k.dienst) : ""}</td>
                      <td>{k.bewegingsCode}</td>
                      <td>
                        {k.dienstregelpuntStart}-{k.spoorStart}
                      </td>
                      <td>
                        {isKlusEindLocatieAnders(k, klusSoorten) ? (
                          <>
                            {k.dienstregelpuntEind}-{k.spoorEind}
                          </>
                        ) : (
                          <>-</>
                        )}
                      </td>
                      <td>{k.toelichting}</td>
                      <td>
                        <tractie-icon
                          name="edit"
                          className={"table-action-button"}
                          data-testid={`action-edit-klus-${k.id}`}
                          color="--tractie-link-color"
                          stroke-width="normal"
                          size="m"
                          onClick={() => {
                            setUpdatableKlus(new CreateKlusModel(k));
                            setIsSidepanelOpen(true);
                            sidepanelRef?.current?.toggleContainer(true);
                          }}
                        ></tractie-icon>
                        <tractie-icon
                          className={"table-action-button"}
                          data-testid={`action-delete-klus-${k.id}`}
                          onClick={() => {
                            if (!k.id) return;
                            setKlusToDelete(k);
                          }}
                          name="trash"
                          color="#D52744"
                          stroke-width="normal"
                          size="m"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <DeleteKlusDialog
              klusToBeDeleted={klusToDelete}
              onClose={() => setKlusToDelete(null)}
              onConfirm={deleteKlus}
            />
          </div>
        </tractie-layout-slot>
        <tractie-layout-slot slot="end" orientation="horizontal">
          <tractie-side-panel
            ref={setSidepanelRef}
            dismissible
            hidden // Hide by default
            className={"sidepanel"}
            data-testid="panel-create-wijzig-klus"
          >
            <CreateKlus
              addKlus={addKlus}
              existingKlus={isSidepanelOpen ? updatableKlus : undefined}
              cancel={handleCloseSidepanel}
            />
          </tractie-side-panel>
        </tractie-layout-slot>
      </tractie-layout>
    </div>
  );
}
